/**
 * Chunked IN Query Utility
 * Purpose: Prevent PostgreSQL "cannot pass more than 100 arguments" error
 * 
 * PostgreSQL has a limit of 100 arguments for IN clauses.
 * This utility automatically chunks large ID arrays and merges results.
 * 
 * @example
 * // Instead of:
 * supabase.from('items').select('*').in('id', largeIdArray)
 * 
 * // Use:
 * chunkedInQuery({
 *   supabase,
 *   table: 'items',
 *   column: 'id',
 *   values: largeIdArray,
 *   selectQuery: '*'
 * })
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { logger } from '../logger';

// Configuration - tunable for different scenarios
export const CHUNKED_QUERY_CONFIG = {
  CHUNK_SIZE: 50,        // Safe buffer below 100 limit
  MAX_CONCURRENT: 5,     // Prevent connection exhaustion
  LOG_THRESHOLD: 100,    // Log warning when IDs exceed this
};

export interface ChunkedInQueryOptions<T = any> {
  supabase: SupabaseClient;
  table: string;
  column: string;
  values: (string | number | null | undefined)[];
  selectQuery?: string;
  additionalFilters?: (query: any) => any;
  orderBy?: { column: string; ascending?: boolean };
}

export interface ChunkedInQueryResult<T> {
  data: T[];
  errors: Error[];
  partialFailure: boolean;
  totalChunks: number;
  successfulChunks: number;
}

/**
 * Execute a chunked IN query to avoid PostgreSQL 100 argument limit
 * Automatically deduplicates, filters null/undefined, and handles partial failures
 */
export async function chunkedInQuery<T = any>(
  options: ChunkedInQueryOptions<T>
): Promise<ChunkedInQueryResult<T>> {
  const { supabase, table, column, values, selectQuery = '*', additionalFilters, orderBy } = options;
  
  // Step 1: Clean and deduplicate values
  const cleanValues = [...new Set(
    values.filter((v): v is string | number => v != null && v !== '')
  )];
  
  // Early return for empty array
  if (cleanValues.length === 0) {
    return {
      data: [],
      errors: [],
      partialFailure: false,
      totalChunks: 0,
      successfulChunks: 0,
    };
  }
  
  // Log warning for large queries
  if (cleanValues.length > CHUNKED_QUERY_CONFIG.LOG_THRESHOLD) {
    logger.warn('[ChunkedInQuery] Large query detected', {
      table,
      column,
      originalCount: values.length,
      cleanCount: cleanValues.length,
    });
  }
  
  // Single query optimization - no chunking needed
  if (cleanValues.length <= CHUNKED_QUERY_CONFIG.CHUNK_SIZE) {
    try {
      let query = supabase.from(table).select(selectQuery);
      
      // eslint-disable-next-line no-restricted-syntax
      query = query.in(column, cleanValues);
      
      if (additionalFilters) {
        query = additionalFilters(query);
      }
      
      if (orderBy) {
        query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true });
      }
      
      const { data, error } = await query;
      
      if (error) {
        return {
          data: [],
          errors: [error],
          partialFailure: false,
          totalChunks: 1,
          successfulChunks: 0,
        };
      }
      
      return {
        data: (data || []) as T[],
        errors: [],
        partialFailure: false,
        totalChunks: 1,
        successfulChunks: 1,
      };
    } catch (error) {
      return {
        data: [],
        errors: [error as Error],
        partialFailure: false,
        totalChunks: 1,
        successfulChunks: 0,
      };
    }
  }
  
  // Step 2: Chunk the values
  const chunks: (string | number)[][] = [];
  for (let i = 0; i < cleanValues.length; i += CHUNKED_QUERY_CONFIG.CHUNK_SIZE) {
    chunks.push(cleanValues.slice(i, i + CHUNKED_QUERY_CONFIG.CHUNK_SIZE));
  }
  
  logger.debug('[ChunkedInQuery] Executing chunked query', {
    table,
    column,
    totalIds: cleanValues.length,
    chunks: chunks.length,
  });
  
  // Step 3: Execute chunks with concurrency control
  const results: T[] = [];
  const errors: Error[] = [];
  let successfulChunks = 0;
  
  // Process chunks in batches of MAX_CONCURRENT
  for (let i = 0; i < chunks.length; i += CHUNKED_QUERY_CONFIG.MAX_CONCURRENT) {
    const batchChunks = chunks.slice(i, i + CHUNKED_QUERY_CONFIG.MAX_CONCURRENT);
    
    const batchPromises = batchChunks.map(async (chunkValues) => {
      try {
        let query = supabase.from(table).select(selectQuery);
        
        // eslint-disable-next-line no-restricted-syntax
        query = query.in(column, chunkValues);
        
        if (additionalFilters) {
          query = additionalFilters(query);
        }
        
        // Note: orderBy is applied per chunk, final sort happens after merge
        const { data, error } = await query;
        
        if (error) {
          return { success: false, error, data: [] };
        }
        
        return { success: true, data: data || [], error: null };
      } catch (error) {
        return { success: false, error: error as Error, data: [] };
      }
    });
    
    const batchResults = await Promise.allSettled(batchPromises);
    
    for (const result of batchResults) {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          results.push(...(result.value.data as T[]));
          successfulChunks++;
        } else if (result.value.error) {
          errors.push(result.value.error);
        }
      } else {
        errors.push(result.reason);
      }
    }
  }
  
  // Step 4: Sort final results if orderBy specified
  if (orderBy && results.length > 0) {
    results.sort((a: any, b: any) => {
      const aVal = a[orderBy.column];
      const bVal = b[orderBy.column];
      
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return orderBy.ascending === false ? -comparison : comparison;
    });
  }
  
  const partialFailure = errors.length > 0 && successfulChunks > 0;
  
  if (partialFailure) {
    logger.warn('[ChunkedInQuery] Partial failure', {
      table,
      column,
      totalChunks: chunks.length,
      successfulChunks,
      errorCount: errors.length,
    });
  }
  
  return {
    data: results,
    errors,
    partialFailure,
    totalChunks: chunks.length,
    successfulChunks,
  };
}

/**
 * Simple wrapper for the most common use case: fetch records by ID array
 * Returns just the data array (throws on complete failure, returns partial on partial failure)
 */
export async function chunkedInQuerySimple<T = any>(
  supabase: SupabaseClient,
  table: string,
  column: string,
  values: (string | number | null | undefined)[],
  selectQuery = '*',
  additionalFilters?: (query: any) => any
): Promise<T[]> {
  const result = await chunkedInQuery<T>({
    supabase,
    table,
    column,
    values,
    selectQuery,
    additionalFilters,
  });
  
  // If complete failure, throw
  if (result.successfulChunks === 0 && result.errors.length > 0) {
    throw result.errors[0];
  }
  
  return result.data;
}

/**
 * Utility to chunk array for manual iteration
 * Useful when you need more control over the query building
 */
export function chunkArray<T>(array: T[], chunkSize = CHUNKED_QUERY_CONFIG.CHUNK_SIZE): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Clean and deduplicate an array of IDs
 * Filters out null, undefined, and empty strings
 */
export function cleanIds<T extends string | number>(
  ids: (T | null | undefined)[]
): T[] {
  return [...new Set(ids.filter((id): id is T => id != null && id !== ''))];
}

/**
 * Execute chunked mutation (update/delete) operations
 * Returns summary of successes and failures
 */
export async function chunkedMutation(
  supabase: SupabaseClient,
  table: string,
  column: string,
  values: (string | number | null | undefined)[],
  operation: 'delete' | 'update',
  updateData?: Record<string, any>
): Promise<{ successCount: number; errorCount: number; errors: Error[] }> {
  const cleanValues = cleanIds(values);
  
  if (cleanValues.length === 0) {
    return { successCount: 0, errorCount: 0, errors: [] };
  }
  
  const chunks = chunkArray(cleanValues);
  let successCount = 0;
  const errors: Error[] = [];
  
  for (let i = 0; i < chunks.length; i += CHUNKED_QUERY_CONFIG.MAX_CONCURRENT) {
    const batchChunks = chunks.slice(i, i + CHUNKED_QUERY_CONFIG.MAX_CONCURRENT);
    
    const batchPromises = batchChunks.map(async (chunkValues) => {
      try {
        let query: any;
        
        if (operation === 'delete') {
          // eslint-disable-next-line no-restricted-syntax
          query = supabase.from(table).delete().in(column, chunkValues);
        } else {
          // eslint-disable-next-line no-restricted-syntax
          query = supabase.from(table).update(updateData!).in(column, chunkValues);
        }
        
        const { error } = await query;
        
        if (error) {
          return { success: false, error, count: 0 };
        }
        
        return { success: true, error: null, count: chunkValues.length };
      } catch (error) {
        return { success: false, error: error as Error, count: 0 };
      }
    });
    
    const batchResults = await Promise.allSettled(batchPromises);
    
    for (const result of batchResults) {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          successCount += result.value.count;
        } else if (result.value.error) {
          errors.push(result.value.error);
        }
      } else {
        errors.push(result.reason);
      }
    }
  }
  
  if (errors.length > 0) {
    logger.warn('[ChunkedMutation] Errors occurred', {
      table,
      operation,
      successCount,
      errorCount: errors.length,
    });
  }
  
  return { successCount, errorCount: errors.length, errors };
}
